/**
 * Chat API Service
 * Handles AI chat operations with health context support
 */

import { http } from "@/lib/http";
import { getAuthToken } from "../../utils/authToken";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

const api = {
  get: async <T>(url: string): Promise<T> => {
    const token = getAuthToken();
    const response = await http.get(`${API_BASE_URL}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data.data;
  },

  post: async <T>(url: string, data: unknown): Promise<T> => {
    const token = getAuthToken();
    const response = await http.post(`${API_BASE_URL}${url}`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data.data;
  },

  delete: async (url: string): Promise<void> => {
    const token = getAuthToken();
    await http.delete(`${API_BASE_URL}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
};

export interface Message {
  id?: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export interface Conversation {
  _id: string;
  userId: string;
  title: string;
  language?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface SendMessageRequest {
  message: string;
  conversationId?: string;
  language?: "en" | "de" | "fr" | "es" | "pt";
  includeHealthContext?: boolean;
}

export interface SendMessageResponse {
  conversationId: string;
  message: string;
}

export interface GetConversationResponse {
  conversation: Conversation;
  messages: Message[];
}

class ChatService {
  /**
   * Get all conversations for current user
   */
  async getConversations(): Promise<Conversation[]> {
    const response = await api.get<{ conversations: Conversation[] }>(
      "/chat/conversations",
    );
    return response.conversations;
  }

  /**
   * Get a single conversation with messages
   */
  async getConversation(
    conversationId: string,
  ): Promise<GetConversationResponse> {
    const response = await api.get<GetConversationResponse>(
      `/chat/conversations/${conversationId}`,
    );
    return response;
  }

  /**
   * Send a message to Tina AI
   */
  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    const response = await api.post<SendMessageResponse>(
      "/chat/message",
      request,
    );
    return response;
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await api.delete(`/chat/conversations/${conversationId}`);
  }

  /**
   * Start a new conversation
   */
  async startConversation(
    firstMessage: string,
    language?: "en" | "de" | "fr" | "es" | "pt",
    includeHealthContext: boolean = true,
  ): Promise<SendMessageResponse> {
    return this.sendMessage({
      message: firstMessage,
      language,
      includeHealthContext,
    });
  }
}

export const chatService = new ChatService();
export default chatService;
